import type { ActivityReportResponse, User } from '@open-dataroom/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** Prefer `{ error: string }` / `{ message: string }` from JSON bodies over raw response text. */
function messageFromErrorResponseBody(
  body: string,
  statusText: string
): string {
  const trimmed = body.trim();
  if (!trimmed) return statusText || 'Request failed';
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.error === 'string' && obj.error) return obj.error;
      if (typeof obj.message === 'string' && obj.message) return obj.message;
    }
  } catch {
    // not JSON — fall through
  }
  if (trimmed.length > 0 && trimmed.length <= 200 && !trimmed.startsWith('<')) {
    return trimmed;
  }
  return statusText || 'Request failed';
}

function resolveFetchUrl(pathOrAbsoluteUrl: string): string {
  if (
    pathOrAbsoluteUrl.startsWith('http://') ||
    pathOrAbsoluteUrl.startsWith('https://')
  ) {
    return pathOrAbsoluteUrl;
  }
  return `${API_BASE}${pathOrAbsoluteUrl}`;
}

function isSameOriginOrApiUrl(pathOrAbsoluteUrl: string): boolean {
  if (
    !pathOrAbsoluteUrl.startsWith('http://') &&
    !pathOrAbsoluteUrl.startsWith('https://')
  ) {
    return true;
  }

  if (typeof window === 'undefined') return false;
  try {
    const target = new URL(pathOrAbsoluteUrl);
    if (target.origin === window.location.origin) return true;
    if (API_BASE) {
      return target.origin === new URL(API_BASE, window.location.origin).origin;
    }
  } catch {
    return false;
  }
  return false;
}

function redirectToLoginOnUnauthorized(path: string): void {
  const isSessionProbe =
    path === '/api/auth/session' || path.startsWith('/api/auth/session?');
  if (
    typeof window !== 'undefined' &&
    window.location.pathname !== '/login' &&
    !isSessionProbe
  ) {
    window.location.href = '/login';
  }
}

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith('dataroom-csrf='));
  return match ? match.slice('dataroom-csrf='.length) : null;
}

function getCsrfToken(): string | null {
  const cookie = readCsrfCookie();
  if (cookie) {
    latestCsrfToken = cookie;
    return cookie;
  }
  return latestCsrfToken;
}

/** In-memory CSRF token synced from API `X-CSRF-Token` response headers. */
let latestCsrfToken: string | null = null;

export function resetCsrfToken(): void {
  latestCsrfToken = null;
}

function syncCsrfFromResponse(res: Response): void {
  const token = res.headers.get('X-CSRF-Token');
  if (token) {
    latestCsrfToken = token;
  }
}

async function fetchJson<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (options.headers) {
    const opts = options.headers;
    if (opts instanceof Headers) {
      opts.forEach((v, k) => {
        headers[k] = v;
      });
    } else if (Array.isArray(opts)) {
      for (const [k, v] of opts) headers[k] = v;
    } else {
      Object.assign(headers, opts as Record<string, string>);
    }
  }
  // Attach CSRF token for state-mutating requests (cookie-authenticated).
  const method = (options.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrf = getCsrfToken();
    if (csrf) {
      headers['X-CSRF-Token'] = csrf;
    }
  }
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers
  });
  syncCsrfFromResponse(res);

  if (res.status === 401) {
    // Session probe is expected to 401 when logged out; never hard-redirect for it
    // (e.g. /auth/verify loads session + verify in parallel — redirect would break magic link).
    redirectToLoginOnUnauthorized(path);
    throw new ApiError('Unauthorized', 401);
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(
      messageFromErrorResponseBody(body, res.statusText),
      res.status
    );
  }

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, init: RequestInit = {}) =>
    fetchJson<T>(path, { method: 'GET', ...init }),
  post: <T>(path: string, body: unknown) =>
    fetchJson<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    fetchJson<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => fetchJson<T>(path, { method: 'DELETE' }),

  // Raw fetch for non-JSON (e.g., file downloads)
  raw: (path: string, options: RequestInit = {}) => {
    const rawHeaders: Record<string, string> = {};
    if (options.headers) {
      const opts = options.headers;
      if (opts instanceof Headers) {
        opts.forEach((v, k) => {
          rawHeaders[k] = v;
        });
      } else if (Array.isArray(opts)) {
        for (const [k, v] of opts) rawHeaders[k] = v;
      } else {
        Object.assign(rawHeaders, opts as Record<string, string>);
      }
    }
    const csrf = getCsrfToken();
    if (csrf) rawHeaders['X-CSRF-Token'] = csrf;
    return fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: rawHeaders
    }).then((res) => {
      syncCsrfFromResponse(res);
      if (res.status === 401) {
        redirectToLoginOnUnauthorized(path);
        throw new ApiError('Unauthorized', 401);
      }
      return res;
    });
  },

  /** PUT file bytes to a presigned R2 URL or same-origin `/api/files/:id/upload-body` (session cookies). */
  uploadPut: (
    presignedOrRelativeUrl: string,
    body: BodyInit,
    contentType: string
  ) => {
    const uploadHeaders: Record<string, string> = {
      'Content-Type': contentType
    };
    const includeCredentials = isSameOriginOrApiUrl(presignedOrRelativeUrl);
    const csrf = includeCredentials ? getCsrfToken() : null;
    if (csrf) uploadHeaders['X-CSRF-Token'] = csrf;
    return fetch(resolveFetchUrl(presignedOrRelativeUrl), {
      method: 'PUT',
      body,
      headers: uploadHeaders,
      credentials: includeCredentials ? 'include' : 'omit'
    }).then((res) => {
      if (includeCredentials) syncCsrfFromResponse(res);
      return res;
    });
  }
};

export { ApiError };

// Types for API responses
export interface SessionResponse {
  user: User;
}

export interface ListedUser {
  id: string;
  email: string;
  role: User['role'];
  createdAt: string;
  lastLoginAt: string | null;
  deactivatedAt: string | null;
}

export interface UsersResponse {
  users: ListedUser[];
}

export interface RoomsResponse {
  rooms: Array<{
    id: string;
    name: string;
    ownerId: string;
    numberingConfig: {
      levels: Array<{ format: string; padding: number }>;
      separator: string;
    };
    createdAt: string;
  }>;
}

export interface RoomResponse {
  room: RoomsResponse['rooms'][0];
}

export interface FoldersResponse {
  folders: Array<{
    id: string;
    roomId: string;
    parentId: string | null;
    name: string;
    displayNumber: string;
    numberingConfig: {
      format?: string;
      padding?: number;
      casing?: string;
      separator?: string;
      levels?: Array<{ format: string; padding: number; casing?: string }>;
    } | null;
    sortOrder: number;
    createdAt: string;
  }>;
}

export interface FilesResponse {
  files: Array<{
    id: string;
    folderId: string;
    roomId: string;
    name: string;
    currentVersion: number;
    r2Key: string;
    mimeType: string;
    sizeBytes: number;
    displayNumber: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  }>;
}

export interface TrashFilesResponse {
  files: Array<
    FilesResponse['files'][0] & {
      permanentDeleteAt: string;
      roomExists: boolean;
    }
  >;
  nextCursor: string | null;
}

export interface ItemsResponse {
  items: Array<
    | (FoldersResponse['folders'][0] & { kind: 'folder' })
    | (FilesResponse['files'][0] & { kind: 'file'; parentId: string | null })
  >;
  total: number;
  nextCursor: string | null;
}

export interface AuditLogItem {
  id: string;
  timestamp: string;
  actorId: string | null;
  actorType: 'user' | 'agent';
  actorEmail: string | null;
  actorRole: User['role'] | null;
  actorDeactivatedAt: string | null;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  roomId: string | null;
  metadata: unknown;
  ipAddress: string | null;
  traceId: string | null;
}

export interface AuditResponse {
  logs: AuditLogItem[];
  nextCursor: string | null;
}

export type ReportsActivityResponse = ActivityReportResponse;

export interface AgentPlansResponse {
  plans: Array<{
    id: string;
    prompt: string;
    status: string;
    result: string | null;
    createdAt: string;
    executedAt: string | null;
  }>;
}

export interface AppSettingsResponse {
  companyName: string;
}

export interface PermissionsResponse {
  permissions: Array<{
    id: string;
    resourceType: string;
    resourceId: string;
    subjectType: string;
    subjectId: string;
    action: string;
    inherited?: boolean;
    inheritedFrom?: {
      permissionId: string;
      resourceType: string;
      resourceId: string;
      action: string;
    };
  }>;
}

export interface SearchResponse {
  results: Array<{
    type: 'file' | 'folder' | 'agent';
    id: string;
    name: string;
    roomId?: string;
    mimeType?: string;
    status?: string;
    matchedTerms: string[];
  }>;
  total: number;
}
